import { useState, useMemo, useCallback } from 'react';
import type { ExpressionGraph } from '../../state/types';

export interface UseExpressionEditorDragOptions {
  expressionGraph: ExpressionGraph;
  rootRef: React.RefObject<HTMLDivElement | null>;
  onDragEndCallback: (event: any) => void;
}

export function useExpressionEditorDrag({
  expressionGraph,
  rootRef,
  onDragEndCallback,
}: UseExpressionEditorDragOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<{
    type: string;
    label: string;
    constantId?: string;
    value?: number | string;
    constantType?: 'number' | 'string';
  } | null>(null);
  const [hoveredPath, setHoveredPath] = useState<string[]>([]);
  const [isPromoteTarget, setIsPromoteTarget] = useState(false);

  const parentMap = useMemo(() => {
    const map = new Map<string, string | null>();
    Object.entries(expressionGraph.expressions).forEach(([id, expr]) => {
      if (expr.type === 'function' && Array.isArray(expr.operandIds)) {
        expr.operandIds.forEach((childId) => {
          if (childId) map.set(childId, id);
        });
      }
    });
    return map;
  }, [expressionGraph]);

  const onDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onDragEnd = useCallback(
    (event: any) => {
      onDragEndCallback(event);
      setDraggedItem(null);
      setIsDragging(false);
      setHoveredPath([]);
    },
    [onDragEndCallback]
  );

  const onDragCancel = useCallback(() => {
    setIsDragging(false);
    setHoveredPath([]);
  }, []);

  const onDragOver = useCallback(
    (event: any) => {
      const { over } = event;

      let deepestBlockId: string | null = null;

      if (over?.id) {
        const overIdStr = String(over.id);

        if (overIdStr.startsWith('function-')) {
          deepestBlockId = overIdStr.replace('function-', '');
        } else if (overIdStr.includes('-slot-')) {
          const match = overIdStr.match(/^(fn-[a-f0-9-]+)-slot-/);
          if (match) deepestBlockId = match[1];
        } else if (overIdStr.startsWith('fn-') && !overIdStr.includes('-slot-')) {
          deepestBlockId = overIdStr;
        }
      }

      const path: string[] = [];
      let current = deepestBlockId;
      while (current) {
        path.unshift(current);
        current = parentMap.get(current) ?? null;
      }

      setHoveredPath(path);

      const overId = event.over?.id;
      const activeType = event.active.data.current?.type;
      if (
        overId === 'expression-canvas' &&
        activeType === 'function' &&
        rootRef.current &&
        event.active.rect?.current?.translated
      ) {
        const bounds = rootRef.current.getBoundingClientRect();
        const translated = event.active.rect.current.translated;
        const centerX = translated.left + translated.width / 2;
        const centerY = translated.top + translated.height / 2;
        const isOutside =
          centerX < bounds.left ||
          centerX > bounds.right ||
          centerY < bounds.top ||
          centerY > bounds.bottom;
        setIsPromoteTarget(isOutside);
      } else {
        setIsPromoteTarget(false);
      }
    },
    [parentMap, rootRef]
  );

  return {
    isDragging,
    draggedItem,
    setDraggedItem,
    hoveredPath,
    isPromoteTarget,
    onDragStart,
    onDragEnd,
    onDragCancel,
    onDragOver,
  };
}
